import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import QuizPage from "@/pages/QuizPage";
import DashboardPage from "@/pages/DashboardPage";
import RhythmPage from "@/pages/RhythmPage";
import JournalPage from "@/pages/JournalPage";
import AffirmationsPage from "@/pages/AffirmationsPage";
import MeditationsPage from "@/pages/MeditationsPage";
import BreathingPage from "@/pages/BreathingPage";
import SpiritChatPage from "@/pages/SpiritChatPage";
import ReadingPage from "@/pages/ReadingPage";
import ListeningPage from "@/pages/ListeningPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import SubscriptionSuccessPage from "@/pages/SubscriptionSuccessPage";
import GuidedPracticesPage from "@/pages/GuidedPracticesPage";
import SettingsPage from "@/pages/SettingsPage";
import AdminPage from "@/pages/AdminPage";
import EmotionalIntelligencePage from "@/pages/EmotionalIntelligencePage";
import ModuleDetailPage from "@/pages/ModuleDetailPage";
import HealthyHabitsPage from "@/pages/HealthyHabitsPage";
import HabitModuleDetailPage from "@/pages/HabitModuleDetailPage";
import ShadowSelfPage from "@/pages/ShadowSelfPage";
import ShadowModuleDetailPage from "@/pages/ShadowModuleDetailPage";
import MoodProgressPage from "@/pages/MoodProgressPage";
import InsightsPage from "@/pages/InsightsPage";
import AncestralWisdomPage from "@/pages/AncestralWisdomPage";
import AncestralModuleDetailPage from "@/pages/AncestralModuleDetailPage";
import WitnessCirclePage from "@/pages/WitnessCirclePage";
import GenogramPage from "@/pages/GenogramPage";
import ArchitectsPortalPage from "@/pages/ArchitectsPortalPage";
import SoundHealingPage from "@/pages/SoundHealingPage";
import SoundHealingDetailPage from "@/pages/SoundHealingDetailPage";
import EnergyMechanicsPage from "@/pages/EnergyMechanicsPage";
import EnergyModuleDetailPage from "@/pages/EnergyModuleDetailPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("auth_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (e) {
          localStorage.removeItem("auth_token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("auth_token", response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, name, referral_code = null) => {
    const payload = { email, password, name };
    if (referral_code) {
      payload.referral_code = referral_code;
    }
    const response = await axios.post(`${API}/auth/register`, payload);
    localStorage.setItem("auth_token", response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("spiritChatSession");
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-breathe">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
            <div className="w-10 h-10 rounded-full bg-primary/40"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  return children;
};

function AppRoutes() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && user && location.pathname === "/auth") {
      if (!user.quiz_completed) {
        navigate("/quiz");
      } else {
        navigate("/dashboard");
      }
    }
  }, [user, loading, location.pathname, navigate]);

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route
        path="/quiz"
        element={
          <ProtectedRoute>
            <QuizPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rhythm"
        element={
          <ProtectedRoute>
            <RhythmPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/journal"
        element={
          <ProtectedRoute>
            <JournalPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/affirmations"
        element={
          <ProtectedRoute>
            <AffirmationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/meditations"
        element={
          <ProtectedRoute>
            <MeditationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/breathing"
        element={
          <ProtectedRoute>
            <BreathingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/spirit"
        element={
          <ProtectedRoute>
            <SpiritChatPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reading"
        element={
          <ProtectedRoute>
            <ReadingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/listening"
        element={
          <ProtectedRoute>
            <ListeningPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            <SubscriptionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription/success"
        element={
          <ProtectedRoute>
            <SubscriptionSuccessPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/guided-practices"
        element={
          <ProtectedRoute>
            <GuidedPracticesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/guided-practices/:meditationId"
        element={
          <ProtectedRoute>
            <GuidedPracticesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/emotional-intelligence"
        element={
          <ProtectedRoute>
            <EmotionalIntelligencePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/emotional-intelligence/:moduleId"
        element={
          <ProtectedRoute>
            <ModuleDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/healthy-habits"
        element={
          <ProtectedRoute>
            <HealthyHabitsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/healthy-habits/:moduleId"
        element={
          <ProtectedRoute>
            <HabitModuleDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/shadow-self"
        element={
          <ProtectedRoute>
            <ShadowSelfPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/shadow-self/:moduleId"
        element={
          <ProtectedRoute>
            <ShadowModuleDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mood-progress"
        element={
          <ProtectedRoute>
            <MoodProgressPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/insights"
        element={
          <ProtectedRoute>
            <InsightsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ancestral-wisdom"
        element={
          <ProtectedRoute>
            <AncestralWisdomPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ancestral-wisdom/:moduleId"
        element={
          <ProtectedRoute>
            <AncestralModuleDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sound-healing"
        element={
          <ProtectedRoute>
            <SoundHealingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sound-healing/:moduleId"
        element={
          <ProtectedRoute>
            <SoundHealingDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/energy-mechanics"
        element={
          <ProtectedRoute>
            <EnergyMechanicsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/energy-mechanics/:moduleId"
        element={
          <ProtectedRoute>
            <EnergyModuleDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/witness-circle"
        element={
          <ProtectedRoute>
            <WitnessCirclePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/genogram"
        element={
          <ProtectedRoute>
            <GenogramPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/architects"
        element={
          <ProtectedRoute>
            <ArchitectsPortalPage />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  // Apply dark mode on initial load
  useEffect(() => {
    const darkMode = localStorage.getItem('darkMode');
    if (darkMode === 'true') {
      document.documentElement.classList.add('dark');
    }
    if (localStorage.getItem('text_size') === 'large') {
      document.documentElement.classList.add('text-large');
    }
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App min-h-screen bg-background">
          <AppRoutes />
          <Toaster position="top-center" richColors />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
